import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Reveal from "@/components/Reveal";
import MeshBg from "@/components/MeshBg";
import Blocks from "@/components/Blocks";
import Gallery from "@/components/Gallery";
import { PARTNERS, PARTNER_META, BRANDS, CAMPANII } from "@/data/content";

export default function Partner() {
  const { slug } = useParams();
  const partner = PARTNERS[slug];

  if (!partner) return <Navigate to="/" replace />;

  const meta = PARTNER_META[slug] || {};
  const summary = meta.summary || (partner.intro && partner.intro[0]);
  const facts = meta.facts || [];
  const others = BRANDS.filter((b) => b.slug !== slug).slice(0, 4);

  return (
    <div data-testid="partner-page" data-partner={slug}>
      {/* HERO */}
      <section className="relative overflow-hidden pt-12 md:pt-16 pb-10 md:pb-14 border-b border-slate-100">
        <MeshBg variant="warm" />
        <div className="container-x">
          <Reveal>
            <Link
              to="/#branduri"
              data-testid="partner-back"
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Toate brandurile
            </Link>
          </Reveal>
          <div className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_0.7fr] lg:items-center">
            <div>
              <Reveal delay={0.05}>
                <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
                  {partner.title}
                </h1>
              </Reveal>
              {summary && (
                <Reveal delay={0.1}>
                  <p className="mt-6 max-w-2xl lead">{summary}</p>
                </Reveal>
              )}
              {facts.length > 0 && (
                <Reveal delay={0.15}>
                  <div className="mt-7 flex flex-wrap gap-2.5" data-testid="partner-facts">
                    {facts.map((f, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-ink shadow-sm"
                      >
                        {f}
                      </span>
                    ))}
                  </div>
                </Reveal>
              )}
            </div>
            {partner.logo && (
              <Reveal delay={0.2}>
                <div className="flex items-center justify-center rounded-3xl border border-slate-100 bg-white p-10 shadow-[0_24px_50px_-30px_rgba(15,23,42,0.25)]">
                  <img src={partner.logo} alt={partner.name} className="max-h-24 max-w-[80%] object-contain" />
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      {partner.video && (
        <section className="pt-10 md:pt-14">
          <div className="container-x">
            <Reveal>
              <div className="mx-auto max-w-4xl rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_30px_60px_-30px_rgba(15,23,42,0.35)]">
                <div className="aspect-video w-full overflow-hidden rounded-xl bg-slate-100">
                  <iframe
                    src={partner.video}
                    title={`Video ${partner.name}`}
                    className="h-full w-full"
                    frameBorder="0"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* SECTIONS */}
      <section className="py-14 md:py-20">
        <div className="container-x">
          {partner.sectionsTitle && (
            <Reveal>
              <span className="overline">Colaborare</span>
              <h2 className="mt-5 text-3xl md:text-5xl font-bold tracking-tight">
                {partner.sectionsTitle}
              </h2>
            </Reveal>
          )}
          <div className="mt-12 space-y-10">
            {partner.sections.map((s, i) => (
              <Reveal key={i} delay={(i % 2) * 0.06}>
                <div className="card grid gap-6 p-8 md:grid-cols-[0.4fr_1.6fr] md:p-10">
                  <div className="flex items-start gap-4">
                    <span className="font-display text-2xl font-extrabold text-brand/30">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="text-xl md:text-2xl font-bold tracking-tight leading-snug">
                      {s.title}
                    </h3>
                  </div>
                  <Blocks items={s.body} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERIES */}
      <section className="bg-white py-12 md:py-16 border-y border-slate-100">
        <div className="container-x space-y-12">
          {partner.galleries.map((g, i) => (
            <div key={i}>
              <Reveal>
                <h3 className="mb-8 text-2xl md:text-3xl font-bold tracking-tight">{g.title}</h3>
              </Reveal>
              <Reveal delay={0.08}>
                <Gallery images={g.images} testid={`partner-gallery-${i}`} />
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      {/* OTHER BRANDS */}
      <section className="py-12 md:py-16">
        <div className="container-x">
          <Reveal>
            <div className="flex items-end justify-between gap-4">
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight">Alte branduri care ne-au ales</h3>
              <Link to="/#branduri" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-brand hover:text-brand-dark transition-colors">
                Vezi toate
              </Link>
            </div>
          </Reveal>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {others.map((b, i) => (
              <Reveal key={b.slug} delay={i * 0.05}>
                <Link
                  to={`/${b.slug}`}
                  data-testid={`partner-other-${b.slug}`}
                  className="group flex h-28 items-center justify-center overflow-hidden rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_8px_24px_-10px_rgba(15,23,42,0.15)] transition-all duration-300 hover:border-brand/30 hover:shadow-[0_20px_44px_-26px_rgba(108,92,231,0.45)]"
                >
                  <img src={b.logo} alt={b.name} className="max-h-full max-w-[75%] object-contain" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-14 md:pb-20">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-brand px-8 py-14 md:px-16 md:py-16 text-center">
              <div className="pointer-events-none absolute -top-16 -left-10 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
              <h2 className="relative mx-auto max-w-2xl text-3xl md:text-4xl font-extrabold tracking-tight text-white">
                {CAMPANII.ctaTitle}
              </h2>
              <div className="relative mt-8">
                <Link to="/contact" data-testid="partner-cta" className="btn px-8 py-4 bg-white text-ink">
                  {CAMPANII.ctaButton}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
